import { useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Reveal } from "./Reveal";
import { EVIDENCE_PAIRS } from "../data/assets";

// Intrinsic dimensions of the evidence photographs (measured from source files).
const PAIR_DIMS = {
  "air-duct": { before: [732, 446], after: [736, 446] },
  "kitchen-exhaust": { before: [733, 445], after: [738, 447] },
  "laundry-exhaust": { before: [726, 449], after: [737, 449] },
  "drain-grease": { before: [731, 446], after: [745, 446] },
  "dry-ice": { before: [720, 444], after: [740, 444] },
};

// BEFORE / AFTER service comparison. Air Duct / HVAC (pairs[0]) is always the
// default active tab; there is NO automatic rotation — the selection changes
// only on explicit user action. Full WAI-ARIA tabs pattern: tablist/tab with
// unique ids, aria-selected/aria-controls, tabpanel with aria-labelledby,
// arrow/Home/End keys with roving tabindex, Enter/Space via native buttons.
export function BeforeAfter({ dark = true, pairs = EVIDENCE_PAIRS }) {
  const [active, setActive] = useState(pairs[0]);
  const reduce = useReducedMotion();
  const tabsRef = useRef(null);

  const onTabsKeyDown = (e) => {
    const ids = pairs.map((p) => p.id);
    const i = ids.indexOf(active.id);
    let next = null;
    if (e.key === "ArrowRight") next = (i + 1) % pairs.length;
    else if (e.key === "ArrowLeft") next = (i - 1 + pairs.length) % pairs.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = pairs.length - 1;
    if (next === null) return;
    e.preventDefault();
    setActive(pairs[next]);
    tabsRef.current?.querySelectorAll('[role="tab"]')[next]?.focus();
  };

  const dims = PAIR_DIMS[active.id] || { before: [736, 446], after: [736, 446] };

  return (
    <div data-testid="before-after-block">
      {pairs.length > 1 && (
      <div
        ref={tabsRef}
        className="flex gap-2 overflow-x-auto whitespace-nowrap sm:flex-wrap sm:overflow-visible sm:whitespace-normal"
        role="tablist"
        aria-label="Service evidence selector"
        onKeyDown={onTabsKeyDown}
      >
        {pairs.map((pair) => (
          <button
            key={pair.id}
            id={`ba-tab-${pair.id}`}
            role="tab"
            aria-selected={active.id === pair.id}
            aria-controls="ba-panel"
            tabIndex={active.id === pair.id ? 0 : -1}
            data-testid={`before-after-tab-${pair.id}`}
            onClick={() => setActive(pair)}
            className={`min-h-[44px] shrink-0 border px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] transition-colors ${
              active.id === pair.id
                ? "border-dcc bg-dcc text-white"
                : dark
                ? "border-zinc-700 text-zinc-400 hover:border-dcc hover:text-dcc"
                : "border-zinc-300 text-zinc-600 hover:border-dcc hover:text-dcc"
            }`}
          >
            {pair.label}
          </button>
        ))}
      </div>
      )}
      <Reveal className={pairs.length > 1 ? "mt-8" : ""}>
        <AnimatePresence mode="wait">
          <motion.div
            key={active.id}
            id="ba-panel"
            role="tabpanel"
            aria-labelledby={`ba-tab-${active.id}`}
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={reduce ? undefined : { opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="grid gap-4 sm:grid-cols-2"
            data-testid="before-after-pair"
          >
            {["before", "after"].map((stage) => (
              <figure key={stage} className={`relative overflow-hidden border ${dark ? "border-zinc-800" : "border-zinc-200"}`}>
                <img
                  src={active[stage].src}
                  alt={active[stage].alt}
                  width={dims[stage][0]}
                  height={dims[stage][1]}
                  loading="eager"
                  decoding="async"
                  className="aspect-[4/3] w-full object-cover"
                />
                <figcaption
                  className={`absolute left-0 top-0 px-4 py-2 font-mono text-xs uppercase tracking-[0.3em] ${
                    stage === "after" ? "bg-dcc text-white" : "bg-charcoal/85 text-zinc-200"
                  }`}
                >
                  {stage}
                </figcaption>
              </figure>
            ))}
          </motion.div>
        </AnimatePresence>
      </Reveal>
      <p className={`mt-4 font-mono text-xs leading-relaxed ${dark ? "text-zinc-400" : "text-zinc-500"}`}>
        // DCC field documentation — camera-inspection photography from live client systems.
      </p>
    </div>
  );
}
